import { notEmpty, theme, useResponsive } from "@product/scmp-sdk";
import { useResizeObserver, useSessionStorageValue } from "@react-hookz/web";
import { minutesToMilliseconds } from "date-fns";
import { useSetAtom } from "jotai";
import { useRouter } from "next/router";
import type { RefObject } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { graphql, readInlineData } from "react-relay";
import { useIdle } from "react-use";
import CreateSeedRandom from "seed-random";

import { Edition } from "~/lib/edition";
import type { hooksPickMorningStudioArticle$key } from "~/queries/__generated__/hooksPickMorningStudioArticle.graphql";

import { whyRegisterHomepagePositionTriggerReferenceAtom } from "./atoms";
import {
  deleteHomepageScrollY,
  getHomepageMultimediaScrollY,
  getHomepageScrollY,
  saveHomepageMultimediaScrollY,
  saveHomepageScrollY,
} from "./helpers";

export const useLayoutWorkaroundForMyNews = (edition: Edition) => {
  const [myNewsElement, setMyNewsElement] = useState<HTMLDivElement | null>();
  const [secondSectionBlockElement, setSecondSectionBlockElement] =
    useState<HTMLDivElement | null>();
  const isHomeDesktop = useResponsive(theme.breakpoints.up("homeDesktop"), false);
  const [isSecondSectionHigherThanMyNews, setIsSecondSectionHigherThanMyNews] = useState(false);

  useResizeObserver(
    secondSectionBlockElement ?? null,
    _ => {
      const secondSectionElementHeight =
        secondSectionBlockElement?.getBoundingClientRect().height ?? 0;
      const myNewsElementHeight = myNewsElement?.getBoundingClientRect().height ?? 0;
      setIsSecondSectionHigherThanMyNews(secondSectionElementHeight > myNewsElementHeight);
    },
    edition === Edition.HongKong && isHomeDesktop,
  );

  return {
    isSecondSectionHigherThanMyNews,
    setMyNewsElement,
    setSecondSectionBlockElement,
  };
};

export const useAutoRefreshWhenIdlingForTenMinutes = () => {
  const isIdle = useIdle(minutesToMilliseconds(10));

  const { setIsAutoRefreshed } = useIsAutoRefreshed();

  useEffect(() => {
    if (!isIdle) {
      return;
    }

    setIsAutoRefreshed("true");
    window.location.reload();
  }, [isIdle, setIsAutoRefreshed]);
};

export const useIsAutoRefreshed = () => {
  const {
    remove: removeIsAutoRefreshed,
    set: setIsAutoRefreshed,
    value: isAutoRefreshed,
  } = useSessionStorageValue<string>("is_auto_refreshed");

  return {
    isAutoRefreshed,
    removeIsAutoRefreshed,
    setIsAutoRefreshed,
  };
};

export const useWhyRegisterHomepagePositionTriggerReference = () => {
  const whyRegisterHomepagePositionTriggerReference = useRef(null);
  const setWhyRegisterHomepagePositionTriggerReference = useSetAtom(
    whyRegisterHomepagePositionTriggerReferenceAtom,
  );

  useEffect(() => {
    if (!whyRegisterHomepagePositionTriggerReference.current) {
      return;
    }
    setWhyRegisterHomepagePositionTriggerReference(whyRegisterHomepagePositionTriggerReference);
  }, [setWhyRegisterHomepagePositionTriggerReference]);

  return whyRegisterHomepagePositionTriggerReference;
};

export const usePickMorningStudioArticles = (
  seed: string,
  reference?: hooksPickMorningStudioArticle$key | null,
) => {
  const random = CreateSeedRandom(seed);
  const data = readInlineData(
    graphql`
      fragment hooksPickMorningStudioArticle on Queue @inline {
        items(first: 9) {
          edges {
            # eslint-disable-next-line relay/unused-fields
            node {
              ...aiArticleListSponsoredArticle
            }
            ...topStoriesQueueItemsEdge
          }
        }
      }
    `,
    reference ?? null,
  );

  const slot1Articles = (data?.items?.edges ?? []).slice(0, 2);
  const slot2Articles = (data?.items?.edges ?? []).slice(2);
  const slot1Picked = Math.floor(random() * slot1Articles.length);
  const slot2Picked = Math.floor(random() * slot2Articles.length);

  return [slot1Articles[slot1Picked], slot2Articles[slot2Picked]];
};

export const useSaveHomepageScrollPosition = () => {
  const router = useRouter();

  useEffect(() => {
    /** leave homepage with client-side routing */
    const handleRouteChangeStart = () => {
      saveHomepageScrollY();
    };

    /** leave homepage with server-side routing */
    const handleBeforeunload = () => {
      saveHomepageScrollY();
    };

    /** leave homepage with server-side routing on iOS Safari */
    const handlePagehide = () => {
      saveHomepageScrollY();
    };

    window.addEventListener("beforeunload", handleBeforeunload);
    window.addEventListener("pagehide", handlePagehide);
    router.events.on("routeChangeStart", handleRouteChangeStart);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeunload);
      window.removeEventListener("pagehide", handlePagehide);
      router.events.off("routeChangeStart", handleRouteChangeStart);
    };
  }, [router]);
};

type UseSaveMultimediaScrollYParameters = {
  multimediaReference: RefObject<HTMLDivElement>;
};

export const useSaveMultimediaScrollY = ({
  multimediaReference,
}: UseSaveMultimediaScrollYParameters) => {
  useEffect(() => {
    const multimediaReferenceCurrent = multimediaReference?.current;
    if (!multimediaReferenceCurrent) {
      return;
    }

    const multimediaScrollY =
      multimediaReferenceCurrent.getBoundingClientRect().top + window.scrollY;
    saveHomepageMultimediaScrollY(multimediaScrollY);
  }, [multimediaReference]);
};

type HomepageScrollYRestorationTriggerTiming = "beforeLazyLoading" | "afterLazyLoading";

type GetShouldTriggerParameters = {
  homepageMultimediaScrollY: number | null;
  homepageScrollY: number | null;
  routerPathname: string | undefined;
  triggerTiming: HomepageScrollYRestorationTriggerTiming;
};

const getShouldRestore = ({
  homepageMultimediaScrollY,
  homepageScrollY,
  routerPathname,
  triggerTiming,
}: GetShouldTriggerParameters) => {
  if (!notEmpty(routerPathname) || !notEmpty(homepageScrollY)) {
    return false;
  }

  if (
    triggerTiming === "afterLazyLoading" &&
    homepageMultimediaScrollY !== null &&
    homepageScrollY >= homepageMultimediaScrollY
  ) {
    return true;
  }

  if (
    triggerTiming === "beforeLazyLoading" &&
    (homepageMultimediaScrollY === null || homepageScrollY < homepageMultimediaScrollY)
  ) {
    return true;
  }

  return false;
};

type UseHomepageScrollYRestorationParameters = {
  triggerTiming: HomepageScrollYRestorationTriggerTiming;
};

export const useHomepageScrollYRestoration = ({
  triggerTiming,
}: UseHomepageScrollYRestorationParameters) => {
  const homepageScrollY = getHomepageScrollY();
  const restoreHomepageScrollY = useCallback(() => {
    if (!notEmpty(homepageScrollY)) {
      return;
    }
    window.scrollTo(0, Number(homepageScrollY));
    deleteHomepageScrollY();
  }, [homepageScrollY]);

  const router = useRouter();

  const shouldRestore = getShouldRestore({
    homepageMultimediaScrollY: getHomepageMultimediaScrollY(),
    homepageScrollY,
    routerPathname: router?.pathname,
    triggerTiming,
  });

  const handleHomepageScrollYRestoration = useCallback(() => {
    if (shouldRestore) {
      restoreHomepageScrollY();
    }
  }, [restoreHomepageScrollY, shouldRestore]);

  useEffect(() => {
    handleHomepageScrollYRestoration();

    /** open homepage with client-side routing */
    const handleRouteChangeComplete = () => {
      handleHomepageScrollYRestoration();
    };

    /** open homepage with server-side routing iOS Safari */
    const handlePageshow: WindowEventHandlers["onpageshow"] = event => {
      const isIOS = /(iPad|iPhone|iPod)/g.test(navigator.userAgent);
      if (!isIOS && event.persisted) {
        return;
      }
      handleHomepageScrollYRestoration();
    };

    router.events.on("routeChangeComplete", handleRouteChangeComplete);
    window.addEventListener("pageshow", handlePageshow);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChangeComplete);
      window.removeEventListener("pageshow", handlePageshow);
    };
  }, [handleHomepageScrollYRestoration, router]);
};
